import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { MovelMeta } from '@visualizador/shared';
import { api } from '../api/client.js';
import { CountdownTimer } from '../components/CountdownTimer.js';
import { materialParaExibir } from '../lib/material.js';
import { formatPrazo, prazoStatus } from '../lib/prazo.js';
import { getTheme, sceneBg } from '../lib/theme.js';
import { PainelDesempenho } from '../components/PainelDesempenho.js';
import { BarraMedir } from '../components/BarraMedir.js';
import { BussolaEixos } from '../components/BussolaEixos.js';
import { lerBolinhaFixa } from '../lib/medir.js';
import {
  adicionarOculta, chaveDaPeca, chaveDoMovel, idsParaEsconder, lerOcultas, limparOcultas,
  ocultasPresentes, rotuloExibirTudo,
} from '../lib/ocultas.js';
import { ListaFolgas } from '../components/ListaFolgas.js';
import { MovelViewer, type ViewerInfo, type PieceMenu, type EstadoMedir, type EixosTela, type Folga } from '../three/viewerCore.js';

type LoadState =
  | { status: 'loading'; text: string }
  | { status: 'ready' }
  | { status: 'error'; text: string }
  | { status: 'ambiguous'; pieceId: string; options: Array<{ movel_id: string; arquivo: string; projeto: string; nome: string }> };

export function ViewerPage() {
  const [params] = useSearchParams();
  const movelParam = params.get('movel') ?? undefined;
  const pecaId = params.get('peca') ?? undefined;
  const projeto = params.get('projeto') ?? undefined;
  const arquivoParam = params.get('arquivo') ?? undefined;

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<MovelViewer | null>(null);
  // ?debug=1 mostra o painel de desempenho (medir no tablet de verdade).
  const [viewerDebug, setViewerDebug] = useState<MovelViewer | null>(null);
  const [debug] = useState(() => new URLSearchParams(window.location.search).get('debug') === '1');
  const ctxRef = useRef<HTMLDivElement | null>(null); // menu de contexto (botao direito)
  // Pecas a destacar assim que o modelo terminar de carregar (fluxo do QR). E uma
  // LISTA porque a etiqueta traz o id da entidade: se a mesma prateleira aparece
  // em 9 modulos iguais, o site marca as 9 em vez de escolher uma.
  const pendingPecasRef = useRef<string[] | undefined>(undefined);

  const [meta, setMeta] = useState<MovelMeta | null>(null);
  // Sobe de 1 a cada modelo carregado: é o gatilho para reesconder as peças
  // ocultas (carregar o .glb zera o que estava escondido dentro do viewer).
  const [modeloPronto, setModeloPronto] = useState(0);
  const [load, setLoad] = useState<LoadState>({ status: 'loading', text: 'Carregando…' });

  const [pieceIds, setPieceIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState('');
  // Todas as ocorrências marcadas em vermelho agora (o painel avisa quando é mais de uma).
  const [destacados, setDestacados] = useState<string[]>([]);
  // Peças ocultadas à mão e gravadas neste navegador (chaves do lib/ocultas).
  const [ocultas, setOcultas] = useState<string[]>([]);
  const [info, setInfo] = useState<ViewerInfo>(null);
  // Folgas da peça selecionada até as vizinhas (as 6 direções), para listar
  // no painel com o NOME da vizinha — no 3D a cota não diz até qual peça é.
  const [folgas, setFolgas] = useState<Folga[]>([]);
  const [idInput, setIdInput] = useState(pecaId ?? '');
  const [fonte, setFonte] = useState('');
  const [medidasVisiveis, setMedidasVisiveis] = useState(true);
  const [menuAberto, setMenuAberto] = useState(false);
  const [medindo, setMedindo] = useState(false);
  // Direção de X/Y/Z na tela (bússola do medir); o viewer só avisa medindo.
  const [eixosTela, setEixosTela] = useState<EixosTela | null>(null);
  const [estadoMedir, setEstadoMedir] = useState<EstadoMedir>(() => ({
    fixa: lerBolinhaFixa(), mira: false, pendente: false, distancia: '', medidas: 0,
    encaixe: '', eixo: 'livre', componentes: '',
  }));
  const [filtroPeca, setFiltroPeca] = useState('');
  const [ocultarFrentes, setOcultarFrentes] = useState(false);
  const [pieceMenu, setPieceMenu] = useState<PieceMenu | null>(null);
  // Ferragens ocultas por padrao tambem no movel sozinho: abre mais leve. Aqui
  // nao existe botao so para elas — quem as traz de volta e o "Exibir tudo"
  // (um movel sozinho e leve); o estado abaixo so serve para mostrar o
  // progresso do download dentro desse botao.
  const [progressoFerragens, setProgressoFerragens] = useState<[number, number] | null>(null);

  // Mapa id -> nome da peca (vem dos metadados do banco).
  const nomePorId = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of meta?.pieces ?? []) m.set(p.id, p.nome);
    return m;
  }, [meta]);

  // Lista de pecas = todas as do projeto (banco) + as que estiverem na cena. Com
  // as ferragens em arquivo separado, parafuso e dobradica so entram na cena
  // quando baixados; pela lista do banco elas continuam aparecendo, e clicar
  // numa delas baixa as ferragens e ja seleciona.
  const idsDaLista = useMemo(() => {
    const doBanco = (meta?.pieces ?? []).map((p) => p.id);
    return [...new Set([...doBanco, ...pieceIds])];
  }, [meta, pieceIds]);

  // Ids das "frentes" (portas, gavetas, frentes, tampos) — pelo nome da peça.
  const frontIds = useMemo(() => {
    const rx = /frente|porta|frente de gaveta|tampo|basculante/i;
    return (meta?.pieces ?? []).filter((p) => rx.test(p.nome)).map((p) => p.id);
  }, [meta]);

  // Cria o viewer 3D uma vez.
  useEffect(() => {
    if (!canvasRef.current) return;
    const viewer = new MovelViewer(canvasRef.current, {
      onPieceList: setPieceIds,
      onSelected: (id, ids) => { setSelectedId(id); setDestacados(ids ?? (id ? [id] : [])); if (id) setIdInput(id); },
      onInfo: setInfo,
      onFolgas: setFolgas,
      onLoaded: (label) => {
        setFonte(label);
        // Destaca a(s) peca(s) escaneada(s) SO depois do modelo carregar e indexar
        // (robusto mesmo em conexao lenta / modelo grande) — em vez de tempo fixo.
        const pecas = pendingPecasRef.current;
        if (pecas?.length) {
          viewer.selectIds(pecas);
          pendingPecasRef.current = undefined;
        }
        // Carregar o modelo zera as peças escondidas: avisa quem as devolve.
        setModeloPronto((n) => n + 1);
      },
      onError: (text) => setLoad({ status: 'error', text }),
      onPieceMenu: (m) => setPieceMenu(m),
      onFerragensProgresso: (feitas, total) => setProgressoFerragens(total ? [feitas, total] : null),
      onMedir: setEstadoMedir,
      onEixosTela: setEixosTela,
    });
    void viewer.setFerragensVisiveis(false);
    viewer.setBolinhaFixa(lerBolinhaFixa());
    viewerRef.current = viewer;
    setViewerDebug(viewer);
    viewer.setBackground(sceneBg(getTheme()));
    return () => { viewer.dispose(); viewerRef.current = null; };
  }, []);

  // Fundo da cena 3D acompanha o tema claro/escuro.
  useEffect(() => {
    const sync = () => viewerRef.current?.setBackground(sceneBg(getTheme()));
    window.addEventListener('themechange', sync);
    return () => window.removeEventListener('themechange', sync);
  }, []);

  // Menu de contexto: clicar fora dele fecha (equivale ao "cancelar").
  useEffect(() => {
    if (!pieceMenu) return;
    const onDown = (e: PointerEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setPieceMenu(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [pieceMenu]);

  // Resolve o movel (via movel= ou peca=) e carrega o .glb no viewer.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let id = movelParam;
        let arquivo = arquivoParam;
        // Ocorrencias que a API mandou para o id do QR (vazio = API/plugin antigos).
        let ocorrenciasDoQr: string[] = [];
        if (!id && pecaId) {
          setLoad({ status: 'loading', text: 'Localizando peça…' });
          const res = await api.resolvePeca(pecaId, { arquivo: arquivoParam, projeto });
          if (res.kind === 'ambiguous') {
            if (!cancelled) setLoad({ status: 'ambiguous', pieceId: pecaId, options: res.options });
            return;
          }
          if (res.kind === 'notfound') {
            if (!cancelled) setLoad({ status: 'error', text: 'Peça não encontrada. Verifique o código.' });
            return;
          }
          id = res.movelId;
          arquivo = res.arquivo; // arquivo do movel resolvido (identidade exata)
          ocorrenciasDoQr = res.pieceIds;
        }
        if (!id) { if (!cancelled) setLoad({ status: 'error', text: 'Nenhum móvel ou peça informado na URL.' }); return; }

        setLoad({ status: 'loading', text: 'Carregando dados do móvel…' });
        const movelMeta = await api.getMovel(id, arquivo);
        if (!movelMeta) { if (!cancelled) setLoad({ status: 'error', text: 'Móvel não encontrado no banco de dados.' }); return; }
        if (cancelled) return;
        setMeta(movelMeta);
        if (!movelMeta.hasGlb) { setLoad({ status: 'error', text: 'Este móvel não possui modelo 3D (.glb).' }); return; }

        // Espera o viewer existir e carrega o modelo do banco.
        const arquivoFinal = movelMeta.arquivo;
        const tryLoad = () => {
          const v = viewerRef.current;
          if (!v) { setTimeout(tryLoad, 50); return; }
          // Marca as pecas do QR para serem destacadas quando o modelo terminar (onLoaded).
          // 1o o que a API respondeu; se ela nao respondeu (API/plugin antigos), procura
          // as ocorrencias nos proprios metadados: `id` da ocorrencia ou `peca_id` da
          // entidade. Em movel de plugin antigo isso acha exatamente a peca pedida.
          const locais = pecaId
            ? movelMeta.pieces.filter((p) => p.id === pecaId || p.peca_id === pecaId).map((p) => p.id)
            : [];
          pendingPecasRef.current = ocorrenciasDoQr.length
            ? ocorrenciasDoQr
            : locais.length ? locais : pecaId ? [pecaId] : undefined;
          v.loadURL(
            api.glbUrl(id!, arquivoFinal, movelMeta.atualizado_em),
            movelMeta.nome || id!,
            movelMeta.ferragensSeparadas ? api.glbUrl(id!, arquivoFinal, movelMeta.atualizado_em, 'ferragens') : undefined,
          );
          setLoad({ status: 'ready' });
        };
        tryLoad();
      } catch {
        if (!cancelled) setLoad({ status: 'error', text: 'Erro ao carregar o móvel.' });
      }
    })();
    return () => { cancelled = true; };
  }, [movelParam, pecaId, projeto, arquivoParam]);

  // Reflete a peça selecionada na barra de endereço (sem recarregar o modelo).
  // Assim a URL fica igual à do QR (com ?peca=) e dá pra copiar/compartilhar o link
  // exato da peça — provando que o QR abre o móvel já com a peça certa.
  useEffect(() => {
    if (load.status !== 'ready' || !meta) return;
    const qs = new URLSearchParams();
    qs.set('movel', meta.movel_id);
    if (meta.arquivo) qs.set('arquivo', meta.arquivo);
    if (selectedId) qs.set('peca', selectedId);
    window.history.replaceState(null, '', `/v?${qs.toString()}`);
  }, [selectedId, meta, load.status]);

  // Chave deste móvel no armazenamento das peças ocultas (vazia enquanto carrega).
  const chaveOcultas = meta ? chaveDoMovel(meta.movel_id, meta.arquivo) : '';

  // Devolve ao viewer as peças que a pessoa tinha escondido antes de recarregar.
  // Roda quando os metadados chegam E o modelo termina de carregar: quem carrega
  // o .glb limpa as peças escondidas, então antes disso não adiantaria esconder.
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || !meta || !modeloPronto) return;
    const gravadas = lerOcultas(chaveOcultas);
    setOcultas(ocultasPresentes(gravadas, meta.pieces));
    // A peça que veio do QR fica visível mesmo se estava escondida: seria muito
    // estranho bipar a etiqueta e não ver nada marcado na tela.
    const marcadas = new Set(v.getDestaque());
    for (const id of idsParaEsconder(gravadas, meta.pieces)) {
      if (!marcadas.has(id)) v.hidePiece(id);
    }
  }, [meta, modeloPronto, chaveOcultas]);

  // Grava uma peça como oculta (pelo caminho_pid quando existe — ver lib/ocultas).
  function gravarOcultaDaPeca(id: string) {
    if (!chaveOcultas) return;
    const peca = meta?.pieces.find((p) => p.id === id);
    setOcultas(adicionarOculta(chaveOcultas, peca ? chaveDaPeca(peca) : id));
  }

  // ---- Comandos do viewer ----
  function alternarMedidas() {
    const novo = !medidasVisiveis;
    setMedidasVisiveis(novo);
    viewerRef.current?.setMeasuresVisible(novo);
  }
  function alternarMedir() {
    const novo = !medindo;
    setMedindo(novo);
    viewerRef.current?.setPointMeasureMode(novo);
    // Tablet/celular: a gaveta cobre o 3D; a barra de medir fica sobre a cena.
    if (novo) setMenuAberto(false);
  }
  function limparMedicoes() {
    viewerRef.current?.clearFreeMeasures();
  }
  function alternarFrentes() {
    const novo = !ocultarFrentes;
    setOcultarFrentes(novo);
    viewerRef.current?.setHiddenPieces(novo ? frontIds : []);
  }
  function marcar(id: string) {
    const v = id.trim();
    if (v) viewerRef.current?.selectId(v);
  }
  // "Exibir tudo": volta o movel ao estado de origem — sai do raio X, devolve as
  // pecas ocultadas uma a uma e traz as ferragens (baixando se ainda nao baixou).
  function exibirTudo() {
    const v = viewerRef.current;
    if (!v) return;
    v.clearSelection();
    v.clearManualHidden(); // volta pecas ocultadas no botao direito
    // Esquece também o que estava gravado: senão a peça voltaria a sumir no
    // próximo carregamento e ninguém entenderia por quê.
    limparOcultas(chaveOcultas);
    setOcultas([]);
    void v.setFerragensVisiveis(true);
    setIdInput('');
  }
  function ocultarPecaMenu() {
    if (pieceMenu) {
      viewerRef.current?.hidePiece(pieceMenu.pieceId);
      gravarOcultaDaPeca(pieceMenu.pieceId);
    }
    setPieceMenu(null);
  }
  // Pelo painel da peca (funciona sem mouse). Some a peca e sai do raio X.
  function ocultarSelecionada() {
    const v = viewerRef.current;
    if (!v || !selectedId) return;
    v.hidePiece(selectedId);
    gravarOcultaDaPeca(selectedId);
    v.clearSelection();
    setIdInput('');
  }

  // ---- Telas sem 3D (erro/ambiguo) ----
  if (load.status === 'ambiguous') {
    return (
      <CenterScreen>
        <div className="error-box" style={{ maxWidth: 420 }}>
          A peça <b>{load.pieceId}</b> existe em mais de um móvel. Escolha:
          <div style={{ marginTop: 10 }}>
            {load.options.map((o) => (
              <Link key={`${o.movel_id}|${o.arquivo}`} className="btn" style={{ display: 'block', margin: '6px 0' }}
                to={`/v?movel=${encodeURIComponent(o.movel_id)}&arquivo=${encodeURIComponent(o.arquivo)}&peca=${encodeURIComponent(load.pieceId)}`}>
                {o.nome || o.movel_id} {o.arquivo ? `— ${o.arquivo}` : ''} {o.projeto ? `(${o.projeto})` : ''}
              </Link>
            ))}
          </div>
        </div>
        <Link className="btn" to="/">Voltar ao início</Link>
      </CenterScreen>
    );
  }
  if (load.status === 'error') {
    return (
      <CenterScreen>
        <div className="error-box" style={{ maxWidth: 420 }}>{load.text}</div>
        <Link className="btn" to="/">Voltar ao início</Link>
      </CenterScreen>
    );
  }

  const selecionada = selectedId ? nomePorId.get(selectedId) : undefined;

  // Medidas gerais do móvel (vindas do plugin) e dims reais da peça selecionada.
  const dimsMovel = (() => {
    const d = meta?.dims;
    if (!d || (!d.largura && !d.altura && !d.profundidade)) return '';
    const n = (v?: number) => (v ? Math.round(v) : '—');
    return `${n(d.largura)} × ${n(d.altura)} × ${n(d.profundidade)} mm`;
  })();

  const pecaSel = selectedId ? meta?.pieces.find((p) => p.id === selectedId) : undefined;
  const materialSel = materialParaExibir(pecaSel, meta?.materiais);

  // Quantas vezes ESTA MESMA peça (a mesma entidade do SketchUp) aparece no
  // móvel. A etiqueta é uma só para as 9 prateleiras iguais, então quem bipa o
  // QR precisa saber que existem 9 — senão marca uma e acha que acabou.
  // Móvel de plugin antigo não tem `peca_id`: fica 1 e a linha nem aparece.
  const ocorrenciasDaSel = pecaSel?.peca_id
    ? (meta?.pieces ?? []).filter((p) => p.peca_id === pecaSel.peca_id).length
    : 1;
  const dimsPecaReal = pecaSel && (pecaSel.dims.c || pecaSel.dims.l || pecaSel.dims.e)
    ? `${Math.round(pecaSel.dims.c)} × ${Math.round(pecaSel.dims.l)} × ${Math.round(pecaSel.dims.e)} mm`
    : '';

  return (
    <div className="vw">
      {/* Botão ☰ — abre o painel de controles no celular/tablet */}
      <button type="button" className="vw-menu-btn" aria-label="Abrir menu" onClick={() => setMenuAberto(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>
      <div
        className={`vw-backdrop ${menuAberto ? 'aberto' : ''}`}
        role="presentation"
        onClick={() => setMenuAberto(false)}
      />

      <aside className={`vw-side ${menuAberto ? 'aberto' : ''}`}>
        <div className="vw-side-head">
          <Link to={meta?.arquivo ? `/?cliente=${encodeURIComponent(meta.arquivo)}` : '/'} className="btn vw-back">← Voltar</Link>
          <div className="vw-titulo">
            <div className="vw-nome">{meta?.nome || meta?.movel_id || 'Móvel'}</div>
            <div className="muted small">{[meta?.projeto, meta?.cliente].filter(Boolean).join(' · ')}</div>
          </div>
          <button type="button" className="vw-side-close" aria-label="Fechar menu" onClick={() => setMenuAberto(false)}>✕</button>
        </div>

        {/* Resumo do móvel: medidas gerais + prazo final */}
        {meta && (
          <div className="vw-resumo">
            {dimsMovel && (
              <div className="vw-resumo-linha">
                <span className="muted small">Medidas (L×A×P)</span>
                <b>{dimsMovel}</b>
              </div>
            )}
            <div className="vw-resumo-linha">
              <span className="muted small">Prazo final</span>
              <b className={`prazo-badge ${prazoStatus(meta.prazo_em)}`}>{formatPrazo(meta.prazo_em, true)}</b>
            </div>
            {meta.projeto && (
              <div className="vw-resumo-linha">
                <span className="muted small">Projeto</span>
                <b>{meta.projeto}</b>
              </div>
            )}
            {meta.cliente && (
              <div className="vw-resumo-linha">
                <span className="muted small">Cliente</span>
                <b>{meta.cliente}</b>
              </div>
            )}
            {meta.empresa && <div className="muted small">{meta.empresa}</div>}
          </div>
        )}

        <button
          type="button"
          className={`vw-toggle-medidas ${medidasVisiveis ? 'on' : ''}`}
          onClick={alternarMedidas}
        >
          {medidasVisiveis ? '🙈 Ocultar medidas' : '📏 Mostrar medidas'}
        </button>

        <button
          type="button"
          className={`vw-toggle-medidas ${medindo ? 'on' : ''}`}
          onClick={alternarMedir}
        >
          {medindo ? '📍 Medindo… (toque para sair)' : '📐 Medir 2 pontos'}
        </button>

        <div className="row vw-linha-frentes">
          <button type="button" className="vw-limpar" onClick={limparMedicoes}>Limpar</button>
          <button
            type="button"
            className={`vw-frentes ${ocultarFrentes ? 'on' : ''}`}
            onClick={alternarFrentes}
          >
            {ocultarFrentes ? '👁 Mostrar frentes' : '🚪 Ocultar frentes'}
          </button>
        </div>

        <div className="row">
          {/* Enquanto as ferragens do "Exibir tudo" estao baixando, o proprio
              botao vira o aviso de progresso (evita apertar duas vezes). */}
          <button type="button" onClick={exibirTudo} disabled={!!progressoFerragens}>
            {progressoFerragens
              ? `⏳ Carregando ferragens… ${progressoFerragens[0] + 1} de ${progressoFerragens[1]}`
              : rotuloExibirTudo(ocultas.length)}
          </button>
          <button type="button" onClick={() => viewerRef.current?.resetCamera()}>Resetar câmera</button>
        </div>

        {/* Input do CÓDIGO da peça (marca direto) */}
        <input
          type="text" placeholder="Código da peça (ex.: P1001)" value={idInput} autoComplete="off"
          onChange={(e) => { setIdInput(e.target.value); marcar(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') marcar(idInput); }}
        />

        {/* Input de BUSCA por nome -> janela rolante com os resultados */}
        <div className="vw-busca">
          <input
            type="search" placeholder="Buscar peça por nome…" value={filtroPeca} autoComplete="off"
            onChange={(e) => setFiltroPeca(e.target.value)}
          />
          {filtroPeca.trim() && (() => {
            const q = filtroPeca.trim().toLowerCase();
            const res = idsDaLista.filter((id) => id.toLowerCase().includes(q) || (nomePorId.get(id) || '').toLowerCase().includes(q));
            return (
              <div className="vw-busca-pop">
                {res.length === 0 ? (
                  <div className="muted small" style={{ padding: 8 }}>Nenhuma peça encontrada.</div>
                ) : res.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`vw-pid ${id === selectedId ? 'active' : ''}`}
                    onClick={() => marcar(id)}
                  >
                    <span className="vw-pid-id">{id}</span>
                    <span className="vw-pid-nome">{nomePorId.get(id) || ''}</span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>

        <hr />
        <div className="muted small">Peças no modelo (clique para marcar):</div>
        <div className="vw-list">
          {idsDaLista.length === 0 ? (
            <div className="muted small">Sem peças nomeadas.</div>
          ) : idsDaLista.map((id) => (
            <button
              key={id}
              type="button"
              className={`vw-pid ${id === selectedId ? 'active' : ''}`}
              onClick={() => { marcar(id); setMenuAberto(false); }}
            >
              <span className="vw-pid-id">{id}</span>
              <span className="vw-pid-nome">{nomePorId.get(id) || ''}</span>
            </button>
          ))}
        </div>
      </aside>

      <div className="vw-view">
        <div ref={canvasRef} className="vw-canvas" />
        {debug && <PainelDesempenho viewer={viewerDebug} />}

        {/* Contador de prazo flutuante */}
        {meta?.prazo_em && <CountdownTimer prazoEm={meta.prazo_em} />}

        {/* Etiqueta da fonte / peca atual */}
        <div className="vw-hint">{fonte || 'Carregando…'}</div>

        {/* Painel de informações da peça selecionada */}
        {info && info.kind === 'piece' && !medindo && (
          <div className="vw-info">
            <b>Peça {info.id}</b>{selecionada ? ` — ${selecionada}` : ''}<br />
            C×L×E: {dimsPecaReal || info.dims}
            {dimsPecaReal && <span className="muted small"> (medida do projeto)</span>}
            {materialSel && <><br />Material: {materialSel}</>}
            {ocorrenciasDaSel > 1 && (
              <><br /><span className="muted small">
                {ocorrenciasDaSel} ocorrências desta peça no móvel
                {destacados.length > 1 ? ' (todas marcadas em vermelho)' : ''}
              </span></>
            )}
            <ListaFolgas
              folgas={folgas}
              nomeDe={(id) => nomePorId.get(id) || ''}
              onEscolher={(id) => marcar(id)}
            />
            <div className="vw-info-acoes">
              <button type="button" className="vw-info-ocultar" onClick={ocultarSelecionada}>🙈 Ocultar peça</button>
            </div>
          </div>
        )}

        {medindo && <BarraMedir viewer={viewerRef.current} estado={estadoMedir} onConcluir={alternarMedir} />}
        {medindo && <BussolaEixos eixos={eixosTela} travado={estadoMedir.eixo} />}

        {load.status === 'loading' && (
          <div className="center-msg">
            <div className="spinner" />
            <div className="muted">{load.text}</div>
          </div>
        )}

        {/* Menu de contexto (botao direito na peca): botao "Ocultar peca".
            Fecha ao clicar fora (listener no document) — sem backdrop nem Cancelar. */}
        {pieceMenu && (
          <div ref={ctxRef} className="vw-ctx" style={{ left: pieceMenu.x, top: pieceMenu.y }}>
            <div className="vw-ctx-nome">
              Peça {pieceMenu.pieceId}
              {nomePorId.get(pieceMenu.pieceId) ? ` — ${nomePorId.get(pieceMenu.pieceId)}` : ''}
            </div>
            <button type="button" className="vw-ctx-btn" onClick={ocultarPecaMenu}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              Ocultar peça
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CenterScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="viewer-root">
      <div className="center-msg">{children}</div>
    </div>
  );
}
